import React, { useRef } from 'react';
import styled from 'styled-components';
import { motion, useInView } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHandshake, 
  faCertificate, 
  faAward, 
  faTruck, 
  faIndustry, 
  faWarehouse, 
  faBoxOpen
} from '@fortawesome/free-solid-svg-icons';
import SectionTitle from '../UI/SectionTitle';

// Основной контейнер
const PartnershipSection = styled.section`
  padding: var(--spacing-xxl) 0;
  background: linear-gradient(135deg, #f9f9f9 0%, #f0f4ff 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%233f51b5' fill-opacity='0.03' fill-rule='evenodd'/%3E%3C/svg%3E");
    opacity: 0.5;
    z-index: 0;
  }
`;

const PartnershipContainer = styled.div`
  max-width: var(--container-width);
  padding: 0 var(--container-padding);
  margin: 0 auto;
  position: relative;
  z-index: 1;
`;

const PartnershipDescription = styled(motion.p)`
  font-size: 1.1rem;
  line-height: 1.7;
  color: #555;
  text-align: center;
  max-width: 800px;
  margin: 0 auto 60px;
`;

// Секция с преимуществами
const AdvantagesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  margin-bottom: 70px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

// Сначала объявляем компонент иконки
const AdvantageIcon = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3f51b5, #5c6bc0);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  color: white;
  font-size: 1.8rem;
  box-shadow: 0 10px 20px rgba(63, 81, 181, 0.2);
  transition: transform 0.6s ease;
  z-index: 2;
`;

const AdvantageCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 30px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: transform 0.4s ease, box-shadow 0.4s ease;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(63, 81, 181, 0.05) 0%, rgba(92, 107, 192, 0.05) 100%);
    opacity: 0;
    transition: opacity 0.4s ease;
    z-index: 1;
    pointer-events: none;
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 35px rgba(63, 81, 181, 0.15);
    
    &::after {
      opacity: 1;
    }
  }
  
  &:hover ${AdvantageIcon} {
    transform: rotateY(180deg);
  }
`;

const AdvantageTitle = styled.h3`
  font-size: 1.3rem;
  color: #333;
  margin-bottom: 15px;
  font-weight: 600;
  transition: color 0.3s ease;
  z-index: 2;
  
  ${AdvantageCard}:hover & {
    color: #3f51b5;
  }
`;

const AdvantageDescription = styled.p`
  font-size: 0.95rem;
  color: #666;
  line-height: 1.6;
  z-index: 2;
  transition: color 0.3s ease;
`;

// Секция с партнерами
const PartnersContainer = styled.div`
  margin-top: 80px;
`;

const PartnersTitle = styled.h3`
  font-size: 1.6rem;
  text-align: center;
  margin-bottom: 40px;
  color: #333;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: linear-gradient(90deg, #3f51b5, #5c6bc0);
    border-radius: 3px;
  }
`;

const PartnersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 40px;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

// Сначала объявляем компонент иконки
const PartnerIcon = styled.div`
  position: absolute;
  top: 30px;
  left: 50%;
  transform: translateX(-50%);
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #3f51b5;
  box-shadow: 0 5px 15px rgba(63, 81, 181, 0.15);
  transition: all 0.3s ease;
`;

const PartnerCard = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  padding: 90px 30px 30px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.4s ease;
  cursor: pointer;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 6px;
    background: linear-gradient(90deg, #3f51b5, #5c6bc0);
    transition: height 0.3s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -100px;
    left: -100px;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(63, 81, 181, 0.1) 0%, rgba(255, 255, 255, 0) 70%);
    border-radius: 50%;
    transition: all 0.5s ease;
    opacity: 0;
    z-index: 0;
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 35px rgba(63, 81, 181, 0.2);
    
    &::before {
      height: 10px;
    }
    
    &::after {
      opacity: 1;
      transform: scale(3);
    }
  }
  
  &:hover ${PartnerIcon} {
    transform: translateX(-50%) scale(1.1);
    color: #ff6b00;
    box-shadow: 0 8px 25px rgba(63, 81, 181, 0.25);
  }
`;

const PartnerType = styled.h4`
  font-size: 1.2rem;
  color: #333;
  margin-top: 10px;
  margin-bottom: 15px;
  text-align: center;
  transition: color 0.3s ease;
  
  ${PartnerCard}:hover & {
    color: #3f51b5;
  }
`;

const PartnerDescription = styled.p`
  font-size: 0.95rem;
  color: #666;
  line-height: 1.6;
  text-align: center;
  transition: color 0.3s ease;
`;

// Секция с лицензиями и сертификатами
const CertificateSection = styled.div`
  margin-top: 80px;
`;

const CertificateContainer = styled(motion.div)`
  background: white;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  display: flex;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const CertificateIconContainer = styled.div`
  flex: 0 0 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 40px;
  
  @media (max-width: 768px) {
    margin-right: 0;
    margin-bottom: 30px;
  }
`;

const CertificateIcon = styled(motion.div)`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: #3f51b5;
  background: #f5f7ff;
  box-shadow: 0 10px 20px rgba(63, 81, 181, 0.1);
`;

const CertificateContent = styled.div`
  flex: 1;
`;

const CertificateTitle = styled.h3`
  font-size: 1.5rem;
  color: #333;
  margin-bottom: 15px;
`;

const CertificateText = styled.p`
  font-size: 1rem;
  color: #666;
  line-height: 1.7;
  margin-bottom: 20px;
`;

const CertificateBadges = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const CertificateBadge = styled.div`
  background: #f0f4ff;
  color: #3f51b5;
  border-radius: 50px;
  padding: 8px 20px;
  font-size: 0.9rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  
  svg {
    font-size: 0.8rem;
  }
`;

// Анимации
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const Partnership = () => {
  const certSectionRef = useRef(null);
  const isCertSectionInView = useInView(certSectionRef, { once: true, amount: 0.2 });
  
  return (
    <PartnershipSection id="partnership">
      <PartnershipContainer>
        <SectionTitle 
          title="Партнерство" 
          subtitle="Сотрудничество с лидерами отрасли"
        />
        
        <PartnershipDescription
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          Компания Купить-Стрейч.РФ гордится сотрудничеством с крупнейшими промышленными предприятиями, заводами и логистическими компаниями. Мы установили прочные партнерские отношения с ведущими производителями стрейч-пленки, что позволяет нам предлагать продукцию высочайшего качества по конкурентным ценам.
        </PartnershipDescription>
        
        <AdvantagesContainer>
          {advantages.map((advantage, index) => (
            <AdvantageCard
              key={index}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              variants={fadeInUp}
            >
              <AdvantageIcon>
                <FontAwesomeIcon icon={advantage.icon} />
              </AdvantageIcon>
              <AdvantageTitle>{advantage.title}</AdvantageTitle>
              <AdvantageDescription>{advantage.description}</AdvantageDescription>
            </AdvantageCard>
          ))}
        </AdvantagesContainer>
        
        <PartnersContainer>
          <PartnersTitle>Наши ключевые партнеры</PartnersTitle>
          <PartnersGrid>
            {partners.map((partner, index) => (
              <PartnerCard
                key={index}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                variants={fadeInUp}
              >
                <PartnerIcon>
                  <FontAwesomeIcon icon={partner.icon} />
                </PartnerIcon>
                <PartnerType>{partner.type}</PartnerType>
                <PartnerDescription>{partner.description}</PartnerDescription>
              </PartnerCard>
            ))}
          </PartnersGrid>
        </PartnersContainer>
        
        <CertificateSection ref={certSectionRef}>
          <CertificateContainer
            initial={{ opacity: 0, scale: 0.95 }}
            animate={isCertSectionInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.8 }}
          >
            <CertificateIconContainer>
              <CertificateIcon
                initial={{ rotate: 0 }}
                animate={isCertSectionInView ? { rotate: 360 } : { rotate: 0 }}
                transition={{ duration: 1.5, ease: "easeInOut" }}
              >
                <FontAwesomeIcon icon={faCertificate} />
              </CertificateIcon>
            </CertificateIconContainer>
            <CertificateContent>
              <CertificateTitle>Качество, подтвержденное сертификатами</CertificateTitle>
              <CertificateText>
                Вся продукция соответствует строгим стандартам качества и безопасности. Партнерство с Купить-Стрейч.РФ — это гарантия качества и надежности.
              </CertificateText>
              <CertificateBadges>
                <CertificateBadge>
                  <FontAwesomeIcon icon={faAward} />
                  ISO 9001
                </CertificateBadge>
                <CertificateBadge>
                  <FontAwesomeIcon icon={faAward} />
                  ГОСТ Р
                </CertificateBadge>
                <CertificateBadge>
                  <FontAwesomeIcon icon={faAward} />
                  Экологический сертификат
                </CertificateBadge>
              </CertificateBadges>
            </CertificateContent>
          </CertificateContainer>
        </CertificateSection>
      </PartnershipContainer>
    </PartnershipSection>
  );
};

// Данные о преимуществах
const advantages = [
  {
    icon: faHandshake,
    title: "Долгосрочное сотрудничество",
    description: "Мы строим долгосрочные отношения с нашими партнерами, основанные на взаимном доверии и выгоде."
  },
  {
    icon: faCertificate,
    title: "Гарантия качества",
    description: "Все наши продукты сертифицированы и соответствуют высоким стандартам качества и безопасности."
  },
  {
    icon: faTruck,
    title: "Стабильные поставки",
    description: "Гарантируем своевременные поставки продукции в любых объемах благодаря налаженным логистическим процессам."
  },
  {
    icon: faIndustry,
    title: "Индивидуальные решения",
    description: "Разрабатываем индивидуальные решения с учетом специфики вашего производства и потребностей."
  }
];

// Данные о партнерах
const partners = [
  {
    icon: faIndustry,
    type: "Производственные предприятия",
    description: "Сотрудничаем с ведущими заводами, обеспечивая их качественными упаковочными материалами для бесперебойной работы производственных линий."
  },
  {
    icon: faWarehouse,
    type: "Логистические центры",
    description: "Обеспечиваем крупные логистические компании и дистрибьюторские центры материалами для надежной упаковки и транспортировки товаров."
  },
  {
    icon: faBoxOpen,
    type: "Торговые сети",
    description: "Поставляем упаковочные решения для торговых сетей и ритейлеров, гарантируя сохранность товаров при хранении и транспортировке."
  }
];

export default Partnership; 